/**
 * RefeedingSuggestionCard — kind-spezifischer Renderer für
 * 'refeeding_risk'-Suggestions.
 *
 * Zeigt:
 *   - Score-Badge (low/moderate/high) + dokumentierte Marker
 *   - die deterministische Faktoren-Liste (NICE-Major/Minor)
 *   - die LLM-Erklärung (`rationale`)
 *   - bis zu 3 Suggested-Actions als Buttons; erste Action ist primär
 *
 * Akzeptanz öffnet den existierenden Refeeding-Plan-Dialog (sobald
 * der Routing-Hook in Phase B.2 fertig ist); für jetzt navigiert
 * 'open_refeeding_dialog' auf die Patienten-Detail-Seite und
 * markiert die Suggestion als accepted.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAiCompanion } from '@/contexts/AiCompanionContext';
import {
  isRefeedingPayload,
  type AiSuggestion,
  type RefeedingSuggestionPayload,
} from '@/lib/ai/types';

interface Props {
  suggestion: AiSuggestion;
}

const RISK_STYLE: Record<RefeedingSuggestionPayload['risk_level'], string> = {
  low: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  moderate:
    'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  high: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-100',
};

const RISK_LABEL: Record<RefeedingSuggestionPayload['risk_level'], string> = {
  low: 'Scorebereich niedrig',
  moderate: 'Scorebereich mittel',
  high: 'Scorebereich hoch',
};

export const RefeedingSuggestionCard: React.FC<Props> = ({ suggestion }) => {
  const navigate = useNavigate();
  const { resolve } = useAiCompanion();

  if (!isRefeedingPayload(suggestion.payload)) {
    return null;
  }
  const p = suggestion.payload;

  const primaryAction = p.suggested_actions[0];
  const secondaryActions = p.suggested_actions.slice(1, 3);

  const handlePrimary = async () => {
    if (primaryAction?.kind === 'open_refeeding_dialog' && suggestion.patient_id) {
      // Pre-fill state for the existing RefeedingDialog is wired in
      // Phase B.2; for now navigate to the patient and mark accepted.
      navigate(`/patients/${suggestion.patient_id}?tab=klinik`);
    }
    await resolve(suggestion, 'accepted', primaryAction?.kind);
  };

  const handleDismiss = async () => {
    await resolve(suggestion, 'dismissed');
  };

  return (
    <article className="border-b p-4 last:border-b-0">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded-md px-2 py-0.5 text-xs font-medium',
              RISK_STYLE[p.risk_level],
            )}
          >
            {RISK_LABEL[p.risk_level]}
          </span>
          <span className="text-xs text-muted-foreground">
            Refeeding-Kriterien · Score {p.deterministic_score}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Hinweis schließen"
          onClick={handleDismiss}
          className="h-6 w-6"
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      {p.extreme_markers && p.extreme_markers.length > 0 && (
        <div className="mb-2 flex items-start gap-1.5 rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-900 dark:bg-rose-950/50 dark:text-rose-100">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Refeeding-Kriterien: dokumentierte Marker vorhanden. Fachlich prüfen.
          </span>
        </div>
      )}

      <ul className="mb-2 space-y-1 text-xs">
        {p.factors.map((f) => (
          <li key={f.key} className="flex items-baseline gap-2">
            <Badge
              variant={f.tier === 'major' ? 'destructive' : 'secondary'}
              className="text-[10px] uppercase tracking-wide"
            >
              {f.tier}
            </Badge>
            <span className="text-foreground">{f.label}</span>
            <span className="text-muted-foreground">— {f.evidence}</span>
          </li>
        ))}
      </ul>

      <p className="mb-3 text-sm text-foreground">{p.rationale}</p>

      <div className="flex flex-wrap items-center gap-2">
        {primaryAction && (
          <Button size="sm" onClick={handlePrimary}>
            {primaryAction.label}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        {secondaryActions.map((a, idx) => (
          <Button
            key={`${a.kind}-${idx}`}
            size="sm"
            variant="outline"
            // Secondary actions are informational only; they neither
            // navigate nor resolve the suggestion. The user reads them
            // and decides whether to accept/dismiss.
            type="button"
            disabled
          >
            {a.label}
          </Button>
        ))}
      </div>
    </article>
  );
};
